// src/features/tuition/components/scheduling/SchedulingCalendar.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Event as EventIcon,
  MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import SessionForm from './SessionForm';
import { 
  fetchSessions, 
  updateSessionStatus, 
  deleteSession 
} from '../../services/sessionsService';
import { fetchStudents } from '../../services/studentsService';

const SchedulingCalendar = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(getCurrentWeekDates());
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState({});
  const [students, setStudents] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  
  useEffect(() => {
    if (userId) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [userId]);
  
  // Effect to update filtered sessions when currentWeek or sessions change
  useEffect(() => {
    if (sessions.length > 0) {
      filterSessionsForCurrentWeek(sessions);
    }
  }, [currentWeek]);
  
  const loadData = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      // Get current date and calculate date range for next few months
      const today = new Date();
      const threeMonthsLater = new Date();
      threeMonthsLater.setMonth(today.getMonth() + 3);
      
      // Fetch all sessions within a broader date range
      const [sessionsData, studentsData] = await Promise.all([
        fetchSessions(userId, {
          // Optional: provide a broader date range if your API supports it
          // startDate: today.toISOString().split('T')[0],
          // endDate: threeMonthsLater.toISOString().split('T')[0]
        }),
        fetchStudents(userId)
      ]);
      
      setSessions(sessionsData);
      setStudents(studentsData);
      
      // Filter for the current week view
      filterSessionsForCurrentWeek(sessionsData);
    } catch (error) {
      console.error("Error loading scheduling data:", error);
    } finally {
      setLoading(false);
    }
  };

  function parseLocalDate(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);           // month is 0-indexed
  }
  
  const filterSessionsForCurrentWeek = (allSessions) => {
    // Get the start and end dates of the week as strings
    const startDateStr = currentWeek[0].date;
    const endDateStr = currentWeek[6].date;
    
    console.log("Filtering for dates between:", startDateStr, "and", endDateStr);
    
    const filtered = allSessions.filter(session => {
      // Ensure session date is treated as a string for comparison
      const sessionDateStr = String(session.date);
      
      // Debug logging to see what's being compared
      console.log("Comparing session date:", sessionDateStr, 
                  "Is within range?", 
                  (sessionDateStr >= startDateStr && sessionDateStr <= endDateStr));
      
      return sessionDateStr >= startDateStr && sessionDateStr <= endDateStr;
    });
    
    console.log("Filtered sessions:", filtered.length, "out of", allSessions.length);
    
    setFilteredSessions(groupSessionsByDay(filtered));
  };
  
  const handleMenuOpen = (event, sessionId) => {
    setAnchorEl(event.currentTarget);
    setSelectedSessionId(sessionId);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSessionId(null);
  };
  
  const handleAddSession = (date) => {
    setCurrentSession(date ? { date } : null);
    setIsFormOpen(true);
  };
  
  const handleEditSession = () => {
    const session = sessions.find(s => s.id === selectedSessionId);
    setCurrentSession(session);
    setIsFormOpen(true);
    handleMenuClose();
  };
  
  const handleDeleteSession = async () => {
    if (!selectedSessionId) return;
    
    if (!window.confirm("Are you sure you want to delete this session?")) {
      handleMenuClose();
      return;
    }
    
    try {
      await deleteSession(selectedSessionId);
      setSessions(sessions.filter(s => s.id !== selectedSessionId));
      handleMenuClose();
    } catch (error) {
      console.error("Error deleting session:", error);
    }
  };
  
  const handleStatusChange = async (newStatus) => {
    if (!selectedSessionId) return;
    
    try {
      await updateSessionStatus(selectedSessionId, newStatus);
      const updatedSessions = sessions.map(s => 
        s.id === selectedSessionId ? { ...s, status: newStatus } : s
      );
      setSessions(updatedSessions);
      handleMenuClose();
    } catch (error) {
      console.error("Error updating session status:", error);
    }
  };
  
  const handleFormClose = () => {
    setIsFormOpen(false);
    setCurrentSession(null);
  };
  
  const handleFormSubmit = async (sessionData) => {
    try {
      if (currentSession && currentSession.id) {
        // Update existing session
        const updatedSessions = sessions.map(s => 
          s.id === currentSession.id ? { ...s, ...sessionData } : s
        );
        setSessions(updatedSessions);
      } else {
        // Add new session
        // In a real implementation, you would call a createSession API here
        // For now, just updating the local state
        const newSession = {
          id: `session_${Date.now()}`,
          ...sessionData,
          status: 'scheduled'
        };
        
        // Add student information
        const student = students.find(s => s.id === sessionData.studentId);
        if (student) {
          newSession.student = {
            firstName: student.firstName,
            lastName: student.lastName
          };
        }
        
        setSessions(prevSessions => [...prevSessions, newSession]);
      }
      
      handleFormClose();
      
      // Refresh data from server
      await loadData();
    } catch (error) {
      console.error("Error saving session:", error);
    }
  };
  
  const goToPreviousWeek = () => {
    // Parse the first day of current week
    const [year, month, day] = currentWeek[0].date.split('-').map(num => parseInt(num, 10));
    const firstDayOfCurrentWeek = new Date(year, month - 1, day); // month is 0-indexed in JS Date
    
    // Create new date for previous week
    const firstDayOfPreviousWeek = new Date(firstDayOfCurrentWeek);
    firstDayOfPreviousWeek.setDate(firstDayOfCurrentWeek.getDate() - 7);
    
    // Generate new week dates
    const newWeek = getWeekDates(firstDayOfPreviousWeek);
    setCurrentWeek(newWeek);
    
    // Re-filter sessions with the updated week
    filterSessionsForCurrentWeek(sessions);
  };

  const goToNextWeek = () => {
    // Parse the first day of current week
    const [year, month, day] = currentWeek[0].date.split('-').map(num => parseInt(num, 10));
    const firstDayOfCurrentWeek = new Date(year, month - 1, day); // month is 0-indexed in JS Date
    
    // Create new date for next week
    const firstDayOfNextWeek = new Date(firstDayOfCurrentWeek);
    firstDayOfNextWeek.setDate(firstDayOfCurrentWeek.getDate() + 7);
    
    // Generate new week dates
    const newWeek = getWeekDates(firstDayOfNextWeek);
    setCurrentWeek(newWeek);
    
    // Re-filter sessions with the updated week
    filterSessionsForCurrentWeek(sessions);
  };
  
  const goToToday = () => {
    setCurrentWeek(getCurrentWeekDates());
  };
  
  function getCurrentWeekDates() {
    return getWeekDates(new Date());
  }
  
  // Revised getWeekDates function with consistent date formatting
  function getWeekDates(dateInput) {
    const date = new Date(dateInput);
    // Set to midnight for consistent day calculation
    date.setHours(0, 0, 0, 0);
    
    // Get day of week (0 = Sunday, 1 = Monday, etc.)
    const day = date.getDay();
    
    // Calculate first day of week (Sunday)
    const diff = date.getDate() - day;
    
    const weekDates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(date);
      currentDate.setDate(diff + i);
      currentDate.setHours(0, 0, 0, 0);
      
      const isToday = currentDate.getTime() === today.getTime();
      
      // Format date consistently using local date components
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const dayNum = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${dayNum}`;
      
      weekDates.push({
        date: dateStr,
        day: currentDate.toLocaleDateString('en-US', { weekday: 'long' }),
        dayShort: currentDate.toLocaleDateString('en-US', { weekday: 'short' }),
        dateNum: currentDate.getDate(),
        month: currentDate.toLocaleDateString('en-US', { month: 'short' }),
        isToday
      });
    }
    
    return weekDates;
  }
  
  function groupSessionsByDay(sessions) {
    const grouped = {};
    
    // Initialize all days with empty arrays
    for (const day of currentWeek) {
      grouped[day.date] = [];
    }
    
    // Properly group sessions by their ISO date string
    for (const session of sessions) {
      // Use the date string directly without timezone conversion
      const sessionDateStr = session.date;
      
      if (grouped[sessionDateStr]) {
        grouped[sessionDateStr].push(session);
      }
    }
    
    return grouped;
  }
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'cancelled': return '#F44336';
      case 'no-show': return '#FF9800';
      default: return '#2196F3';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckIcon sx={{ fontSize: 16, color: '#4CAF50' }} />;
      case 'cancelled': return <CancelIcon sx={{ fontSize: 16, color: '#F44336' }} />;
      case 'no-show': return <PersonIcon sx={{ fontSize: 16, color: '#FF9800' }} />;
      default: return <EventIcon sx={{ fontSize: 16, color: '#2196F3' }} />;
    }
  };
  
  // Helper function to format student names
  const getStudentDisplay = (session) => {
    if (session.studentNames && session.studentNames.length > 0) {
      return session.studentNames.join(', ');
    } else if (session.students && session.students.length > 0) {
      return session.students.map(s => `${s.firstName} ${s.lastName}`).join(', ');
    } else if (session.student) {
      return `${session.student.firstName} ${session.student.lastName || ''}`;
    } else {
      return 'Unnamed Session';
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress sx={{ color: THEME_COLORS.primary }} />
      </Box>
    );
  }
  
  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            Session Calendar
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleAddSession()}
            sx={{
              backgroundColor: THEME_COLORS.primary,
              '&:hover': { backgroundColor: '#1e1c4e' }
            }}
          >
            Add Session
          </Button>
        </Box>
        
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <IconButton onClick={goToPreviousWeek}>
              <ChevronLeftIcon />
            </IconButton>
            
            <Typography variant="h6" fontWeight="medium" sx={{ mx: 1 }}>
              {currentWeek[0].month} {currentWeek[0].dateNum} - {currentWeek[6].month} {currentWeek[6].dateNum}
            </Typography>
            
            <IconButton onClick={goToNextWeek}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<TodayIcon />}
            onClick={goToToday}
            sx={{
              borderColor: THEME_COLORS.primary,
              color: THEME_COLORS.primary,
              '&:hover': {
                borderColor: '#1e1c4e',
                backgroundColor: 'rgba(39, 35, 97, 0.04)'
              }
            }}
          >
            Today
          </Button>
        </Box>
      </Paper>
      
      <Grid container spacing={2}>
        {currentWeek.map((day) => (
          <Grid item xs={12} sm={6} md={day.day === 'Saturday' || day.day === 'Sunday' ? 6 : 4} key={day.date}>
            <Paper
              elevation={1}
              sx={{
                height: '100%',
                borderRadius: 2,
                borderTop: '4px solid',
                borderColor: day.isToday ? THEME_COLORS.primary : 'transparent'
              }}
            >
              <Box
                sx={{
                  bgcolor: day.isToday ? 'rgba(39, 35, 97, 0.04)' : 'transparent',
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {day.day}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {day.month} {day.dateNum}
                  </Typography>
                </Box>
                
                <IconButton 
                  size="small" 
                  onClick={() => handleAddSession(day.date)}
                  sx={{ 
                    bgcolor: 'rgba(39, 35, 97, 0.08)',
                    '&:hover': { bgcolor: 'rgba(39, 35, 97, 0.12)' }
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <Divider />
              
              <Box sx={{ p: 2, minHeight: 150 }}>
                {filteredSessions[day.date] && filteredSessions[day.date].length > 0 ? (
                  filteredSessions[day.date]
                    .sort((a, b) => a.startTime.localeCompare(b.startTime))
                    .map((session) => (
                      <Card
                        key={session.id}
                        variant="outlined"
                        sx={{
                          mb: 2,
                          borderRadius: 1,
                          borderLeft: '4px solid',
                          borderColor: getStatusColor(session.status),
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Typography variant="subtitle2" fontWeight="bold">
                              {getStudentDisplay(session)}
                            </Typography>
                            
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleMenuOpen(e, session.id)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          
                          <Box display="flex" alignItems="center" mt={0.5} mb={1}>
                            <Chip
                              label={session.subject}
                              size="small"
                              sx={{ 
                                height: 20, 
                                bgcolor: 'rgba(39, 35, 97, 0.08)',
                                '& .MuiChip-label': { px: 1, fontSize: '0.7rem' }
                              }}
                            />
                          </Box>
                          
                          <Box display="flex" alignItems="center">
                            <ScheduleIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {session.startTime} - {session.endTime} ({session.duration} min)
                            </Typography>
                          </Box>
                          
                          {session.location && (
                            <Typography variant="body2" color="text.secondary" noWrap>
                              Location: {session.location}
                            </Typography>
                          )}
                          
                          {session.status !== 'scheduled' && (
                            <Box display="flex" alignItems="center" mt={0.5}>
                              {getStatusIcon(session.status)}
                              <Typography variant="caption" sx={{ ml: 0.5, textTransform: 'capitalize' }}>
                                {session.status}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <Typography variant="body2" color="text.secondary">
                      No sessions
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditSession}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Session</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('completed')}>
          <ListItemIcon>
            <CheckIcon fontSize="small" sx={{ color: '#4CAF50' }} />
          </ListItemIcon>
          <ListItemText>Mark as Completed</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('no-show')}>
          <ListItemIcon>
            <PersonIcon fontSize="small" sx={{ color: '#FF9800' }} />
          </ListItemIcon>
          <ListItemText>Mark as No-Show</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('cancelled')}>
          <ListItemIcon>
            <CancelIcon fontSize="small" sx={{ color: '#F44336' }} />
          </ListItemIcon>
          <ListItemText>Cancel Session</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteSession}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: '#F44336' }} />
          </ListItemIcon>
          <ListItemText sx={{ color: '#F44336' }}>Delete Session</ListItemText>
        </MenuItem>
      </Menu>
      
      <SessionForm
        open={isFormOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        initialData={currentSession}
        students={students}
      />
    </Box>
  );
};

export default SchedulingCalendar;